import { FormattedMessage } from 'react-intl';

import FormattedCountryName from '@app/components/FormattedCountryName';
import useMediaQuery from '@app/helpers/useMediaQuery';
import { screenSmall } from '@app/styles/theme/breakpoints';

import type { AddressDetailsFragment } from './__graphql__/OrderHistoryDetailContext';
import { alignEnd, DetailTable } from './OrderHistoryDetail.styles';
import { useOrderHistoryDetail } from './OrderHistoryDetailContext';
import messages from './PaymentDetail.messages';
import { AddressContainer, ItemsSimplified } from './PaymentDetail.styles';

const PaymentDetail = () => {
  const isMobile = useMediaQuery(`(max-width: ${screenSmall})`, {
    ssrMatch: true,
  });
  const { paymentData, hasHardware } = useOrderHistoryDetail();

  const billingAddress = paymentData?.billingAddress && (
    <Address {...paymentData.billingAddress} />
  );

  const paymentMethod = paymentData?.paymentMethods?.[0]?.name;

  const shippingAddress = hasHardware && paymentData?.shippingAddress && (
    <Address {...paymentData.shippingAddress} />
  );

  const shippingMethod = hasHardware && paymentData?.shippingMethod;

  if (isMobile) {
    return (
      <ItemsSimplified>
        <dt>
          <FormattedMessage {...messages.billingAddress} />
        </dt>
        <dd data-testid="billing-address">{billingAddress}</dd>
        {shippingAddress ? (
          <>
            <dt>
              <FormattedMessage {...messages.shippingAddress} />
            </dt>
            <dd data-testid="shipping-address">{shippingAddress}</dd>
          </>
        ) : null}
        {shippingMethod ? (
          <>
            <dt>
              <FormattedMessage {...messages.delivery} />
            </dt>
            <dd data-testid="shipping-method">{shippingMethod}</dd>
          </>
        ) : null}
        <dt>
          <FormattedMessage {...messages.payment} />
        </dt>
        <dd data-testid="payment-method">{paymentMethod}</dd>
      </ItemsSimplified>
    );
  }

  return (
    <DetailTable>
      <thead>
        <tr>
          <th scope="col">
            <FormattedMessage {...messages.billingAddress} />
          </th>
          {hasHardware ? (
            <>
              <th scope="col">
                <FormattedMessage {...messages.shippingAddress} />
              </th>
              <th scope="col" css={alignEnd}>
                <FormattedMessage {...messages.delivery} />
              </th>
            </>
          ) : null}
          <th scope="col" css={alignEnd}>
            <FormattedMessage {...messages.payment} />
          </th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td data-testid="billing-address">{billingAddress}</td>
          {hasHardware ? (
            <>
              <td data-testid="shipping-address">{shippingAddress}</td>
              <td css={alignEnd} data-testid="shipping-method">
                {shippingMethod}
              </td>
            </>
          ) : null}
          <td css={alignEnd} data-testid="payment-method">
            {paymentMethod}
          </td>
        </tr>
      </tbody>
    </DetailTable>
  );
};

export default PaymentDetail;

const Address = (addressData: AddressDetailsFragment) => (
  <AddressContainer>
    <span data-testid="address-fullname">
      {addressData.prefix ? (
        <span data-testid="address-prefix">{addressData.prefix} </span>
      ) : (
        ''
      )}
      {addressData.firstname ? (
        <span data-testid="address-firstname">{addressData.firstname} </span>
      ) : (
        ''
      )}
      {addressData.middlename ? (
        <span data-testid="address-middlename">{addressData.middlename} </span>
      ) : (
        ''
      )}
      {addressData.lastname ? (
        <span data-testid="address-lastname">{addressData.lastname} </span>
      ) : (
        ''
      )}
      {addressData.suffix ? (
        <span data-testid="address-suffix">{addressData.suffix}</span>
      ) : (
        ''
      )}
    </span>

    {addressData.company ? (
      <span data-testid="address-company">{addressData.company}</span>
    ) : null}

    <span data-testid="address-street">{addressData.street}</span>
    <span>
      {addressData.city ? (
        <span data-testid="address-city">{addressData.city} </span>
      ) : (
        ''
      )}
      {addressData.region ? (
        <span data-testid="address-region">{addressData.region} </span>
      ) : (
        ''
      )}
      {addressData.postcode ? (
        <span data-testid="address-postcode">{addressData.postcode} </span>
      ) : (
        ''
      )}
    </span>
    <span data-testid="address-country">
      <FormattedCountryName code={addressData.countryCode} />
    </span>

    {addressData.telephone ? (
      <span data-testid="address-phone">{addressData.telephone}</span>
    ) : null}
  </AddressContainer>
);
